import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { contactInfo, socialMedia } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Results', path: '/results' },
    { name: 'Sales Proof', path: '/salesproof' }
  ];

  const services = [
    'Digital Marketing Strategy',
    'Social Media Marketing',
    'Paid Advertising',
    'SEO Services',
    'Website Optimization',
    'Brand Promotion',
    'Ebook & Book Promotion',
    'Shopify Development'
  ];

  return (
    <footer className="bg-[#0D234E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-1 min-w-0">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/logo.png" 
                alt="DMARKETINGCHAMP Logo" 
                className="h-8 xl:h-10 w-auto shrink-0"
                data-testid="footer-logo"
              />
              <div className="min-w-0">
                <div className="text-sm font-bold" data-testid="footer-brand">DMARKETINGCHAMP</div>
                <div className="text-xs text-[#BFB47E]" data-testid="footer-tagline">...Your Network, Your Future</div>
              </div>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              Your trusted digital marketing partner helping businesses grow, sell, and scale online.
            </p>
            <div className="flex space-x-3">
              <a 
                href={socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
                aria-label="Visit our Facebook page"
                data-testid="footer-social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href={socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
                aria-label="Follow us on Twitter"
                data-testid="footer-social-twitter"
              >
                <Twitter size={18} />
              </a>
              <a 
                href={socialMedia.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
                aria-label="Connect on LinkedIn"
                data-testid="footer-social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href={socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
                aria-label="Follow us on Instagram"
                data-testid="footer-social-instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#BFB47E]">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-300 hover:text-[#BFB47E] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" data-testid="footer-nav-blog" className="text-gray-300 hover:text-[#BFB47E] transition-colors text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-nav-contact" className="text-gray-300 hover:text-[#BFB47E] transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#BFB47E]">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <span className="text-gray-300 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#BFB47E]">Get In Touch</h3>
            <div className="space-y-3">
              <a
                href="mailto:Dmarketingchamp@gmail.com"
                data-testid="footer-email-link"
                className="flex items-center space-x-2 text-gray-300 hover:text-[#BFB47E] transition-colors text-sm"
              >
                <Mail size={18} className="shrink-0" />
                <span className="min-w-0 break-all">Dmarketingchamp@gmail.com</span>
              </a>
              <a
                href={contactInfo.whatsapp}
                data-testid="footer-whatsapp-link"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-300 hover:text-[#BFB47E] transition-colors text-sm"
              >
                <MessageCircle size={18} />
                <span data-testid="footer-contact-number">{contactInfo.whatsappDisplay}</span>
              </a>
              <div className="pt-4">
                <a
                  href={contactInfo.whatsapp}
                  data-testid="footer-book-call-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#BFB47E] text-[#0D234E] px-6 py-2.5 rounded-md font-semibold hover:bg-[#d4c991] transition-all duration-200"
                >
                  Book a Call
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {currentYear} DMARKETINGCHAMP. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
